import { HelmetDexteel } from "@dexteel/mesf-core";
import { Box, Grid2 as Grid, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmationDialogRaw } from "../../../../controls/modals/ConfirmationDialogRaw";
import { SimpleDialogRaw } from "../../../../controls/modals/SimpleDialogRaw";
import { CATSKILL_WHITE } from "../../../../controls/shared/constants/colors";
import { LinkCasterProcess } from "../../caster-processes/components/LinkCasterProcess";
import { ProcessData } from "../../models/ProcessData";
import { useCastersContext } from "../CastersContext";
import { DeliveryTicketAlert } from "./alerts/DeliveryTicketAlert";
import { BilletTempChart } from "./charts/billet-temp/BilletTempChart";
import { MoldLevelChart } from "./charts/mold-level/MoldLevelChart";
import { StrandSpeedChart } from "./charts/strand-speed/StrandSpeedsChart";
import { HeaderComponent } from "./header/HeaderComponent";
import { useLinkUnlinkProcesses } from "./hooks/useLinkUnlinkProcesses";
import { DeliveryTicketModal } from "./modals/delivery-ticket/DeliveryTicketModal";
import { PourbackModal } from "./modals/pourback-modal/PourbackModal";
import { ElementTable } from "./tables/element-table/ElementTable";
import { HousingTable } from "./tables/housing-table/HousingTable";
import { LostStrandTable } from "./tables/lost-strand-table/LostStrandTable";
import { ProbesTable } from "./tables/probes-table/ProbesTable";
import { ProductionTable } from "./tables/production-table/ProductionTable";
import { ScrapTable } from "./tables/scrap-table/ScrapTable";
import { StrandTable } from "./tables/strand-table/StrandTable";
import { CommentsComponent } from "./ui/CommentsComponent";
import { PourbackFields } from "./ui/PourbackFields";

type Props = {
	reloadProcess: Function;
	calculateProcess: Function;
	refreshData: Function;
};
export const DataEntryPageV2 = ({
	reloadProcess,
	calculateProcess,
	refreshData,
}: Props) => {
	const { state, actions } = useCastersContext();
	const navigate = useNavigate();
	const [helmetTitle, setHelmetTitle] = useState("");
	const process = state.processData || ({} as ProcessData);
	const tundishShrouds = state.tundishShrouds || [];
	const ladles = state.ladles || [];
	const grades = state.grades || [];
	const tundishes = state.tundishes || [];
	const tonnageReasons = state.tonnageReasons || [];

	const [deliveryTicketModalOpen, setDeliveryTicketModalOpen] = useState(false);
	const [pourbackModalOpen, setPourbackModalOpen] = useState(false);
	const [selectedProcessIdForModal, setSelectedProcessIdForModal] = useState<
		number | null
	>(null);

	const processesWithoutDeliveryTicket =
		state.processesWithoutDeliveryTicket || [];

	const {
		onProcessClicked,
		unlinkCasterProcess,
		showChangeModal,
		showLinkModal,
		showUnlinkModal,
		setShowChangeModal,
		setShowLinkModal,
	} = useLinkUnlinkProcesses({ refreshData, setHelmetTitle });

	const showAppBar = useMemo(() => {
		return processesWithoutDeliveryTicket.length > 0;
	}, [processesWithoutDeliveryTicket]);

	const handleDeclareClick = (processId: number) => {
		// If the process is the current one, open modal
		if (processId === process.ProcessId) {
			setSelectedProcessIdForModal(processId);
			setDeliveryTicketModalOpen(true);
		} else {
			// If it's a different process, navigate to Production Management with modal open flag
			const selectedProcess = processesWithoutDeliveryTicket.find(
				(p) => p.ProcessId === processId,
			);
			if (selectedProcess) {
				navigate(
					`/production-management/${selectedProcess.ShiftId}?processId=${selectedProcess.ProcessId}&openModal=deliveryTicket`,
				);
			}
		}
	};

	return (
		<>
			<HelmetDexteel
				title={`${process.HeatName ? `${process.HeatName} - ` : ""}Caster Data Entry`}
			/>
			{helmetTitle && <HelmetDexteel title={`${helmetTitle}`} />}
			{showAppBar && processesWithoutDeliveryTicket.length > 0 && (
				<DeliveryTicketAlert
					processes={processesWithoutDeliveryTicket}
					onDeclareClick={handleDeclareClick}
				/>
			)}
			<Box padding={1} style={{ backgroundColor: CATSKILL_WHITE }}>
				<Grid container spacing={1} direction="column">
					<Grid>
						<Typography variant="h5" style={{ fontWeight: 600 }}>
							Caster Data Entry
						</Typography>
					</Grid>
					<Grid>
						<HeaderComponent
							process={process}
							ladles={ladles}
							grades={grades}
							tundishes={tundishes}
							tundishShrouds={tundishShrouds}
							tonnageReasons={tonnageReasons}
							reloadProcess={reloadProcess}
							calculateProcess={calculateProcess}
							refreshData={refreshData}
							onProcessClicked={onProcessClicked}
							setDeliveryTicketModalOpen={setDeliveryTicketModalOpen}
						/>
					</Grid>
					{/* Tables Section */}
					<Grid
						size={{ xs: 12 }}
						container
						spacing={1}
						justifyContent="space-around"
						alignItems="flex-start"
					>
						{/* Column 1: Housing, Production, Scrapped Pieces */}
						<Grid
							size={{ xs: 12, md: 3 }}
							container
							spacing={1}
							alignContent="flex-start"
						>
							<Grid size={{ xs: 12 }}>
								<HousingTable refreshData={refreshData} />
							</Grid>

							<Grid size={{ xs: 12 }}>
								<ProductionTable
									refresh={refreshData}
									readonly={!!process.HeatId ? false : true}
									key={process.ProcessId}
								/>
							</Grid>

							<Grid size={{ xs: 12 }}>
								<ScrapTable refreshData={refreshData} />
							</Grid>
						</Grid>

						{/* Column 2: Strand Casting Speed Chart, Probes */}
						<Grid
							size={{ xs: 12, md: 3 }}
							container
							spacing={1}
							alignContent="flex-start"
						>
							<Grid size={{ xs: 12 }}>
								<StrandSpeedChart key={process.ProcessId} />
							</Grid>
							<Grid size={{ xs: 12 }}>
								<ProbesTable refreshData={refreshData} />
							</Grid>
							<Grid size={{ xs: 12 }}>
								<PourbackFields
									reloadProcess={reloadProcess}
									refreshData={refreshData}
									setPourbackModalOpen={setPourbackModalOpen}
								/>
							</Grid>
						</Grid>

						{/* Column 3: Mold Level Chart, First Half of Strand Table */}
						<Grid
							size={{ xs: 12, md: 6 }}
							container
							spacing={1}
							alignContent="flex-start"
						>
							<Grid size={{ xs: 6 }}>
								<MoldLevelChart key={process.ProcessId} />
							</Grid>
							<Grid size={{ xs: 6 }}>
								<BilletTempChart key={process.ProcessId} />
							</Grid>
							<Grid size={{ xs: 12 }}>
								<StrandTable refreshData={refreshData} />
							</Grid>
						</Grid>
					</Grid>
					{/* Footer Section */}
					<Grid size={{ xs: 12 }} container spacing={1}>
						<Grid size={{ xs: 12, md: 9 }}>
							<ElementTable key={process.ProcessId} />
						</Grid>
						<Grid size={{ xs: 12, md: 3 }}>
							<LostStrandTable
								refreshData={refreshData}
								key={process.ProcessId}
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<CommentsComponent
								reloadProcess={reloadProcess}
								refreshData={refreshData}
								key={process.ProcessId}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Box>

			{/* MODALS */}
			<SimpleDialogRaw
				title="Change Modal"
				message={""}
				open={showChangeModal}
				onClose={() => setShowChangeModal(false)}
			/>
			{/* Pourback Modal */}
			<PourbackModal
				show={pourbackModalOpen}
				onHide={(shouldUpdate) => {
					setPourbackModalOpen(false);
					if (shouldUpdate) refreshData();
				}}
				process={process}
			/>
			{(process.ProcessId || selectedProcessIdForModal) && (
				<DeliveryTicketModal
					show={deliveryTicketModalOpen}
					onHide={(shouldUpdate) => {
						setDeliveryTicketModalOpen(false);
						setSelectedProcessIdForModal(null);
						if (shouldUpdate) {
							refreshData();
						}
					}}
					processId={(selectedProcessIdForModal || process.ProcessId)!}
				/>
			)}
			<ConfirmationDialogRaw
				title="Confirmation"
				message={showUnlinkModal.message}
				open={showUnlinkModal.show}
				onClose={unlinkCasterProcess}
			/>
			<LinkCasterProcess
				show={showLinkModal}
				onHide={(shouldUpdate) => {
					setShowLinkModal(false);
					if (shouldUpdate) reloadProcess();
				}}
				process={process as any}
			/>
		</>
	);
};
